


// client/src/pages/Investors/InvestorUploadAuth.jsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "/api";

const InvestorUploadAuth = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [investorEmail, setInvestorEmail] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [investorName, setInvestorName] = useState("");
  const [series, setSeries] = useState("");
  const [folio, setFolio] = useState("");

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const [loading, setLoading] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [error, setError] = useState("");

  /*
  |--------------------------------------------------------------------------
  | STEP 1
  | Load and validate the secure document request token
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  | The backend already has this route:
  |
  | GET /api/investor/document-request/:token
  |
  | Therefore we MUST use that route here.
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    const validateLink = async () => {
      try {
        setLoading(true);
        setError("");

        if (!token) {
          throw new Error("Invalid investor submission link.");
        }

        const response = await fetch(
          `${API_BASE_URL}/investor/document-request/${encodeURIComponent(
            token
          )}`
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(
            data.message || "This investor submission link is invalid."
          );
        }

        /*
         * Backend response structure:
         *
         * {
         *   success: true,
         *   request: {
         *     investorName,
         *     investorEmail,
         *     series,
         *     folio,
         *     requiredDocuments
         *   }
         * }
         */

        const request = data.request;

        if (!request) {
          throw new Error(
            "Unable to load investor document request."
          );
        }

        setRegisteredEmail(request.investorEmail || "");
        setInvestorEmail(request.investorEmail || "");
        setInvestorName(request.investorName || "");
        setSeries(request.series || "");
        setFolio(request.folio || "");
      } catch (err) {
        console.error(
          "Investor link validation failed:",
          err
        );

        setError(
          err.message ||
            "This link is invalid or has expired. Please contact the NCD Operations Team."
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validateLink();
    } else {
      setLoading(false);
      setError("Invalid investor submission link.");
    }
  }, [token]);

  /*
  |--------------------------------------------------------------------------
  | STEP 2
  | Send OTP
  |--------------------------------------------------------------------------
  |
  | Backend route:
  |
  | POST /api/investor/document-request/:token/send-otp
  |
  | IMPORTANT:
  | token is now a URL parameter.
  |--------------------------------------------------------------------------
  */
  const handleSendOtp = async (e) => {
    e.preventDefault();

    const email = investorEmail.trim().toLowerCase();

    if (!email) {
      toast.error("Please enter your registered email.");
      return;
    }

    if (
      registeredEmail &&
      email !== registeredEmail.trim().toLowerCase()
    ) {
      toast.error(
        "Please enter the registered investor email."
      );
      return;
    }

    if (!token) {
      toast.error("Invalid investor submission link.");
      return;
    }

    try {
      setSendingOtp(true);
      setError("");

      const response = await fetch(
        `${API_BASE_URL}/investor/document-request/${encodeURIComponent(
          token
        )}/send-otp`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            investorEmail: email,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || "Unable to send OTP."
        );
      }

      setOtp("");
      setOtpSent(true);

      toast.success(
        "OTP has been sent to your registered email."
      );
    } catch (err) {
      console.error("Send OTP error:", err);

      toast.error(
        err.message || "Unable to send OTP."
      );
    } finally {
      setSendingOtp(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | STEP 3
  | Verify OTP
  |--------------------------------------------------------------------------
  |
  | Backend route:
  |
  | POST /api/investor/document-request/:token/verify-otp
  |--------------------------------------------------------------------------
  */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    const email = investorEmail.trim().toLowerCase();
    const enteredOtp = otp.trim();

    if (!email) {
      toast.error("Registered email is required.");
      return;
    }

    if (!enteredOtp) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (enteredOtp.length !== 6) {
      toast.error(
        "Please enter the complete 6-digit OTP."
      );
      return;
    }

    if (!token) {
      toast.error("Invalid investor submission link.");
      return;
    }

    try {
      setVerifyingOtp(true);
      setError("");

      const response = await fetch(
        `${API_BASE_URL}/investor/document-request/${encodeURIComponent(
          token
        )}/verify-otp`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            investorEmail: email,
            otp: enteredOtp,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || "Invalid OTP."
        );
      }

      /*
       * Backend response:
       *
       * {
       *   success: true,
       *   authenticated: true,
       *   investor: {
       *     investorName,
       *     investorEmail,
       *     series,
       *     folio
       *   }
       * }
       */

      const investor = data.investor || {};

      /*
       * DEMO ONLY:
       *
       * Store temporary authentication information
       * in sessionStorage.
       */
      sessionStorage.setItem(
        "investorDocumentAuth",
        JSON.stringify({
          authenticated: true,
          token,

          investorEmail:
            investor.investorEmail || email,

          investorName:
            investor.investorName || investorName,

          series:
            investor.series || series,

          folio:
            investor.folio || folio,

          authenticatedAt: Date.now(),
        })
      );

      toast.success(
        "Email verified successfully."
      );

      /*
       * IMPORTANT:
       *
       * App.js defines:
       *
       * /investor-upload/:token/document
       *
       * Therefore navigate to "document".
       */
      navigate(
        `/investor-upload/${encodeURIComponent(
          token
        )}/document`,
        {
          replace: true,
        }
      );
    } catch (err) {
      console.error(
        "OTP verification error:",
        err
      );

      toast.error(
        err.message ||
          "OTP verification failed."
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.loader}>
            Loading secure investor link...
          </div>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Invalid Link
  |--------------------------------------------------------------------------
  */
  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>
            Invalid Submission Link
          </h1>

          <p style={styles.errorText}>
            {error}
          </p>

          <p style={styles.description}>
            Please use the latest link received in
            your investor email or contact the NCD
            Operations Team.
          </p>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Investor Authentication Screen
  |--------------------------------------------------------------------------
  */
  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>
            Investor Verification
          </h1>

          <p style={styles.description}>
            Verify your registered email address to
            continue with your TDS declaration.
          </p>
        </div>

        {/* Investor Information */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            Investor Information
          </h2>

          <div style={styles.infoGrid}>

            <div>
              <div style={styles.label}>
                Investor Name
              </div>

              <div style={styles.value}>
                {investorName || "-"}
              </div>
            </div>

            <div>
              <div style={styles.label}>
                Series
              </div>

              <div style={styles.value}>
                {series || "-"}
              </div>
            </div>

            <div>
              <div style={styles.label}>
                Folio
              </div>

              <div style={styles.value}>
                {folio || "-"}
              </div>
            </div>

          </div>
        </div>

        {/* OTP Authentication */}
        <div style={styles.card}>

          {!otpSent ? (
            <>
              <h2 style={styles.cardTitle}>
                Verify Registered Email
              </h2>

              <p style={styles.description}>
                Enter the email address registered
                against your investor record. An OTP
                will be sent to this registered email
                address.
              </p>

              <form onSubmit={handleSendOtp}>

                <label style={styles.inputLabel}>
                  Registered Email
                </label>

                <input
                  type="email"
                  value={investorEmail}
                  onChange={(e) =>
                    setInvestorEmail(
                      e.target.value
                    )
                  }
                  placeholder="Enter your registered email"
                  style={styles.input}
                  required
                  autoComplete="email"
                />

                <button
                  type="submit"
                  disabled={sendingOtp}
                  style={{
                    ...styles.button,
                    opacity: sendingOtp
                      ? 0.7
                      : 1,
                  }}
                >
                  {sendingOtp
                    ? "Sending OTP..."
                    : "Send OTP"}
                </button>

              </form>
            </>
          ) : (
            <>
              <h2 style={styles.cardTitle}>
                Enter OTP
              </h2>

              <p style={styles.description}>
                A verification OTP has been sent to
                your registered email address.
              </p>

              <p style={styles.email}>
                {investorEmail}
              </p>

              <form onSubmit={handleVerifyOtp}>

                <label style={styles.inputLabel}>
                  OTP
                </label>

                <input
                  type="text"
                  value={otp}
                  onChange={(e) =>
                    setOtp(
                      e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6)
                    )
                  }
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  style={styles.input}
                  required
                />

                <button
                  type="submit"
                  disabled={
                    verifyingOtp ||
                    otp.length !== 6
                  }
                  style={{
                    ...styles.button,
                    opacity:
                      verifyingOtp ||
                      otp.length !== 6
                        ? 0.7
                        : 1,
                  }}
                >
                  {verifyingOtp
                    ? "Verifying..."
                    : "Verify & Continue"}
                </button>

              </form>

              <button
                type="button"
                onClick={() => {
                  setOtp("");
                  setOtpSent(false);
                }}
                style={styles.secondaryButton}
                disabled={verifyingOtp}
              >
                Change Email / Resend OTP
              </button>
            </>
          )}

        </div>

        {/* TDS Remark */}
        <div style={styles.remark}>
          <strong>Important:</strong>

          <p>
            By default, standard TDS shall apply.
          </p>

          <p>
            If you are eligible for lower or nil TDS,
            please submit the applicable document
            after verification.
          </p>
        </div>

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f7f9fc",
    padding: "50px 20px",
  },

  container: {
    maxWidth: "850px",
    margin: "0 auto",
  },

  header: {
    marginBottom: "25px",
  },

  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#171c2b",
    marginBottom: "10px",
  },

  description: {
    color: "#74809a",
    lineHeight: "1.6",
  },

  card: {
    background: "#fff",
    border: "1px solid #e1e6ef",
    borderRadius: "12px",
    padding: "28px",
    marginBottom: "20px",
    boxShadow:
      "0 2px 8px rgba(0,0,0,0.03)",
  },

  cardTitle: {
    fontSize: "20px",
    marginBottom: "20px",
    color: "#171c2b",
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(3, 1fr)",
    gap: "20px",
  },

  label: {
    fontSize: "13px",
    color: "#8490a7",
    marginBottom: "6px",
  },

  value: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#171c2b",
  },

  inputLabel: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "8px",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 14px",
    border: "1px solid #d8deea",
    borderRadius: "8px",
    fontSize: "15px",
    marginBottom: "18px",
    outline: "none",
  },

  button: {
    width: "100%",
    border: "none",
    borderRadius: "8px",
    padding: "13px 18px",
    background: "#1f3a8a",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },

  secondaryButton: {
    width: "100%",
    border: "none",
    background: "transparent",
    color: "#1f3a8a",
    padding: "14px",
    marginTop: "10px",
    cursor: "pointer",
  },

  email: {
    fontWeight: "600",
    color: "#1f3a8a",
    marginBottom: "20px",
  },

  remark: {
    background: "#eef5ff",
    border: "1px solid #d6e5ff",
    borderRadius: "10px",
    padding: "18px",
    color: "#334155",
    lineHeight: "1.5",
  },

  errorText: {
    color: "#b42318",
    fontWeight: "600",
  },

  loader: {
    textAlign: "center",
    color: "#667085",
  },
};

export default InvestorUploadAuth;